import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface KeycloakExchangeResult {
  token: string;
  accessToken: string;
}

/**
 * The one endpoint in the whole API that authenticates with a Keycloak bearer instead of the
 * internal BackOffice JWT.
 *
 * Exported because two interceptor layers must agree on it exactly: KeycloakInterceptor attaches
 * the Keycloak bearer here, and NB_AUTH_TOKEN_INTERCEPTOR_FILTER suppresses the internal JWT here
 * so the two do not overwrite each other. Kept in one place so the allow-list and the skip-list
 * cannot drift apart — when they drifted, every API call went out with the wrong credential.
 */
export const KEYCLOAK_EXCHANGE_URL = `${environment.apiAuth}/ExchangeKeycloakToken`;

/**
 * Case-insensitive because the ASP.NET route matches case-insensitively, so a caller could reach
 * the same action with different casing and slip past a strict comparison.
 */
export function isKeycloakExchangeRequest(url: string): boolean {
  return !!url && url.toLowerCase().startsWith(KEYCLOAK_EXCHANGE_URL.toLowerCase());
}

/**
 * Trades a Keycloak session for an internal BackOffice token pair.
 *
 * No request body and no identity argument on purpose: the backend reads the user from the validated
 * Keycloak token only. Passing a username would make the endpoint an impersonation vector, so there
 * is deliberately no way to express one from here.
 *
 * The Keycloak bearer is attached by KeycloakInterceptor, which allow-lists this exact endpoint via
 * isKeycloakExchangeRequest, so no explicit header handling is needed here.
 */
@Injectable({ providedIn: 'root' })
export class KeycloakExchangeService {
  constructor(private http: HttpClient) { }

  exchange(): Observable<KeycloakExchangeResult> {
    return this.http.post<KeycloakExchangeResult>(KEYCLOAK_EXCHANGE_URL, {});
  }
}
